import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Package, CircleNotch, DownloadSimple } from "@phosphor-icons/react";

const inputCls = "h-7 px-1.5 rounded-[2px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]";
const selCls = "h-7 px-1 rounded-[2px] bg-[var(--bg)] border border-[var(--border)] font-mono text-[11px] text-[var(--text)]";

export const DeliverablesPack = ({ clientName, clientStandards }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [points, setPoints] = useState([]);

  const [incPoints, setIncPoints] = useState(true);
  const [order, setOrder] = useState("PNEZD");
  const [jobName, setJobName] = useState("");
  const [incProfile, setIncProfile] = useState(false);
  const [startPt, setStartPt] = useState("");
  const [endPt, setEndPt] = useState("");
  const [pWidth, setPWidth] = useState("24");
  const [pSlope, setPSlope] = useState("2");
  const [incLot, setIncLot] = useState(false);
  const [legal, setLegal] = useState("");

  const loadPoints = async () => {
    try { setPoints(await api.cogoPoints()); } catch (e) { console.debug("cogoPoints load failed (best-effort)", e); }
  };

  const build = async () => {
    const hasProfile = incProfile && startPt && endPt;
    const hasLot = incLot && legal.trim();
    if (!incPoints && !hasProfile && !hasLot) { toast.error("Pick at least one item to include."); return; }
    setBusy(true);
    const t = toast.loading("Assembling deliverables…");
    try {
      const project = { firm: clientName || "", logo: clientStandards?.branding?.logo || "", client: clientName || "" };
      let lot = null;
      if (hasLot) {
        toast.loading("Parsing the legal description…", { id: t });
        const parsed = await api.parseLegal(legal.trim());
        if (!parsed.courses?.length) { toast.error("Could not parse the legal description.", { id: t }); setBusy(false); return; }
        lot = { courses: parsed.courses, method: "compass", adjust: true, project };
      }
      const profile = hasProfile
        ? { start_id: startPt, end_id: endPt, width: Number(pWidth) || 0, side_slope: Number(pSlope) || 0 }
        : null;
      toast.loading("Building the ZIP…", { id: t });
      const blob = await api.deliverablesPack({ include_points: incPoints, points_order: order, profile, lot, project, job_name: jobName, cover: true });
      const slug = (jobName || clientName || "deliverables").trim().replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "deliverables";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${slug}-deliverables.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Deliverables ZIP downloaded", { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build the deliverables pack.", { id: t });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) loadPoints(); }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="deliverables-btn"
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-2)]">
          <Package size={14} weight="bold" /> Deliverables Pack
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-lg rounded-[4px]" data-testid="deliverables-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><Package size={18} weight="bold" className="text-[var(--primary)]" />Deliverables Pack</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">Bundle the point file, road profile sheet and closure report into a single client ZIP.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2" data-testid="del-jobname-row">
            <span className="text-[11px] font-mono text-[var(--text-dim)] w-20">Job / title</span>
            <Input value={jobName} onChange={(e) => setJobName(e.target.value)} data-testid="del-job-name"
              placeholder="e.g. Smith-Lot4 (names the ZIP + cover sheet)" className={`${inputCls} flex-1`} />
          </div>
          <div className="border border-[var(--border)] rounded-[3px] p-2 flex items-center gap-2" data-testid="del-points-row">
            <input type="checkbox" checked={incPoints} onChange={(e) => setIncPoints(e.target.checked)} data-testid="del-inc-points" className="accent-[var(--primary)]" />
            <span className="text-[12px] font-mono flex-1">COGO points CSV <span className="text-[var(--text-dim)]">({points.length} saved)</span></span>
            <select value={order} onChange={(e) => setOrder(e.target.value)} data-testid="del-points-order" className={selCls}>
              <option value="PNEZD">PNEZD</option><option value="PENZD">PENZD</option>
            </select>
          </div>

          <div className="border border-[var(--border)] rounded-[3px] p-2 flex flex-col gap-2" data-testid="del-profile-row">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={incProfile} onChange={(e) => setIncProfile(e.target.checked)} data-testid="del-inc-profile" className="accent-[var(--primary)]" />
              <span className="text-[12px] font-mono">Road profile sheet (from stored points)</span>
            </label>
            {incProfile && (
              <div className="flex flex-wrap items-center gap-1.5 pl-6">
                <select value={startPt} onChange={(e) => setStartPt(e.target.value)} data-testid="del-start-pt" className={`${selCls} max-w-[100px]`}>
                  <option value="">start…</option>{points.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <select value={endPt} onChange={(e) => setEndPt(e.target.value)} data-testid="del-end-pt" className={`${selCls} max-w-[100px]`}>
                  <option value="">end…</option>{points.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <span className="text-[10px] font-mono text-[var(--text-dim)]">W</span>
                <Input value={pWidth} onChange={(e) => setPWidth(e.target.value)} className={`${inputCls} w-14`} data-testid="del-width" />
                <span className="text-[10px] font-mono text-[var(--text-dim)]">slope</span>
                <Input value={pSlope} onChange={(e) => setPSlope(e.target.value)} className={`${inputCls} w-12`} data-testid="del-slope" />
              </div>
            )}
          </div>

          <div className="border border-[var(--border)] rounded-[3px] p-2 flex flex-col gap-2" data-testid="del-lot-row">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={incLot} onChange={(e) => setIncLot(e.target.checked)} data-testid="del-inc-lot" className="accent-[var(--primary)]" />
              <span className="text-[12px] font-mono">Closure report (paste a legal description)</span>
            </label>
            {incLot && (
              <Textarea value={legal} onChange={(e) => setLegal(e.target.value)} data-testid="del-legal"
                placeholder="Beginning at a point…; thence N45°30'20&quot;E 150.00 feet; thence…"
                className="min-h-[70px] resize-none rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
            )}
          </div>

          <Button onClick={build} disabled={busy} data-testid="del-build-btn"
            className="h-9 rounded-[3px] gap-2 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
            {busy ? <CircleNotch size={15} className="animate-spin" /> : <DownloadSimple size={15} />} Download deliverables ZIP
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
